import { useQuery } from '@tanstack/react-query';

import { tryCatch } from '../../../../../helpers';
import {
  HttpError,
  Maybe,
  Optional,
  WebsheetTemplateProps,
} from '../../../../../types';
import { Device, Plan, SubscriptionType, WatchNotification } from '../types';

interface IParams extends Pick<WebsheetTemplateProps, 'services'> {
  state: Optional<string>;
}
// BE WebsheetWatchTransferGetResponse serializes with
// response_model_exclude_none: Optional fields are absent, not null.
interface IPlanTransferResponse {
  plan?: Plan;
  requires_tc: boolean;
  source_watch_name?: string;
  target_watch_name?: string;
  // 'linked' (sa-watch / standalone) vs 'paired' (Multi-SIM / One Number).
  // A missing attribute defaults to 'paired' so existing journeys are untouched.
  subscription_type?: SubscriptionType;
}

interface ICarrierPlanResponse {
  watch_image_url: Maybe<string>;
  requires_tc: boolean;
  force_websheet: Maybe<any>;
  requires_subscriber_authentication: boolean;
  requires_watch_transfer_tc: boolean;
  plans?: Plan[];
  statusCode?: number;
  subscription_type?: SubscriptionType;
  notification?: WatchNotification;
}

interface IWatchManageResponse {
  requires_tc: boolean;
  devices: Device[];
  plans: Plan[];
  subscription_type?: SubscriptionType;
}

export const useCarrierPlans = ({ services, state }: IParams) => {
  return useQuery({
    queryKey: ['carriers_plan', state, services],
    queryFn: async () => {
      const { data: result, error } = await tryCatch<
        ICarrierPlanResponse & { data?: ICarrierPlanResponse },
        Error
      >(services.fetchCarrierPlans({ state }));

      if (error) {
        throw new HttpError(error.message, (error as any).statusCode);
      }

      return result;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useFetchWatchManage = ({ services, state }: IParams) => {
  return useQuery({
    queryKey: ['fetch_watch_manage', state, services],
    queryFn: async () => {
      const { data, error } = await tryCatch<IWatchManageResponse, Error>(
        services.fetchWatchManage({ state }),
      );

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    retry: 0,
    refetchOnWindowFocus: false,
    select: data => {
      if (data) {
        // Convert plans array to Map with id as key
        const plansMap = new Map(data.plans.map(plan => [plan.id, plan]));

        // Convert devices array to Map with iccid as key
        const devicesMap = new Map(
          data.devices.map(device => [device.iccid, device]),
        );

        const filteredData =
          data?.devices?.filter(({ plan_offer_id }) => plan_offer_id) || [];

        return {
          ...data,
          devices: filteredData,
          plansMap,
          devicesMap,
          hasDevices: filteredData.length > 0,
          hasPlans: data.plans.length > 0,
        };
      }

      return {
        devices: [] as Device[],
        plans: [] as Plan[],
        plansMap: new Map(),
        devicesMap: new Map(),
        requires_tc: data.requires_tc,
        subscription_type: data?.subscription_type,
        hasDevices: false,
        hasPlans: false,
      };
    },
  });
};

export const useFetchTransferPlan = ({ services, state }: IParams) => {
  return useQuery({
    queryKey: ['fetch_watch_transfer', state, services],
    queryFn: async () => {
      const { data, error } = await tryCatch<IPlanTransferResponse, Error>(
        services.fetchWatchTransfer({ state }),
      );

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });
};
